"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import { useFansUnitedSDK } from "fansunited-data-layer/client";
import { useAuth } from "@/lib/auth/AuthContext";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { consentsForSurface, type ConsentDef, type ConsentSurface } from "./types";
import { getAcceptedConsentIds, submitConsents, richTextToPlainText } from "./profile-consent-utils";

export interface UseConsentsReturn {
    /** Set of consentIds already accepted by this user. `null` while loading or signed out. */
    accepted: Set<string> | null;
    loading: boolean;
    /** All enabled consents (required + optional) configured for this surface. */
    available: ConsentDef[];
    /** Required consents at this surface that have not yet been accepted. */
    missingRequired: ConsentDef[];
    /** Submit consentIds to the backend; merges into local state on success. */
    submit: (consentIds: string[]) => Promise<void>;
    /** Re-fetch accepted consents from the backend. */
    refresh: () => Promise<void>;
}

/**
 * Per-surface profile consent state.
 *
 * Reads tenant configuration from `useTenantSettings()` (already client-safe — no extra fetch)
 * and accepted IDs from the FansUnited SDK on mount. Tracks auth state so it resets on sign-out
 * and re-fetches on sign-in / token change.
 */
export function useConsents(surface: ConsentSurface): UseConsentsReturn {
    const sdk = useFansUnitedSDK();
    const { user } = useAuth();
    const { consents } = useTenantSettings();

    const [accepted, setAccepted] = useState<Set<string> | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    const available = useMemo<ConsentDef[]>(() => consentsForSurface(consents, surface), [consents, surface]);

    const refresh = useCallback(async () => {
        if (!sdk || !user) {
            setAccepted(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const ids = await getAcceptedConsentIds(sdk);
            setAccepted(ids);
        } finally {
            setLoading(false);
        }
    }, [sdk, user]);

    useEffect(() => {
        if (!user) {
            setAccepted(null);
            setLoading(false);
            return;
        }
        void refresh();
    }, [user, refresh]);

    const submit = useCallback(
        async (consentIds: string[]) => {
            if (consentIds.length === 0) return;
            const submissions = consentIds.map((id) => {
                const def = available.find((c) => c.consentId === id);
                return {
                    consentId: id,
                    content: def ? richTextToPlainText(def.body) : null,
                };
            });
            await submitConsents(sdk, submissions);
            setAccepted((prev) => {
                const next = new Set(prev ?? []);
                consentIds.forEach((id) => next.add(id));
                return next;
            });
        },
        [sdk, available]
    );

    const missingRequired = useMemo<ConsentDef[]>(() => {
        if (!accepted) return available.filter((c) => c.required);
        return available.filter((c) => c.required && !accepted.has(c.consentId));
    }, [available, accepted]);

    return { accepted, loading, available, missingRequired, submit, refresh };
}
